import { ProgramNodeMap, ProgramNodeDateMap, OnsiteActivity, OnsiteSchedule, OnsiteTimelineBrick, ProgramNode } from '../store/onsite.data';
import { DateSchedule, Program } from '../store/programs.data';
import { getDefaultTimeZoneDetails, str2Date, flatten, getSet, typedValues, jsonCopyObject } from '../helpers/helpers';
import { getOnsiteSchedules } from './onsite.getoss';
import { ShareOSSInfo } from './../store/share.data';
import { sortOnsiteTimelineBrick } from '../store/reselectors/onsite.reselector.filtering';
import { filterTimelineByFields, filterShareableByExcludedOSSFields, filterPerformersByExcludedOSSFieldsAndFilters, filterOnsiteTimelineFilters, filterSponsorsByExcludedOSSFieldsAndFilters, filterTimeline } from './onsite.filtering';
import { Performer } from '../store/performers.data';
import { JoiEvent, LabelMaps, Ranking, Venue } from '../store/events.data';
import { WorkforceGroup, WFWorker } from '../store/workforce.data';
import { FilterGroup } from '../store/filters.data';
import { OnsiteSingleTimeline } from '../../globals/store/onsite.data';
import { Sponsor } from '../store/sponsors.data';
import { Exhibitor } from '../store/exhibitors.data';
import { getValidSortedStagingRequests } from '../store/share.brief.functions';
import { ExhibitorStand } from '../store/exhibitors.data';
import { Asset } from '../store/assets.data';
import { JoiResource } from './../store/resources.data';
import { getAllTagPanelData, TagPanelData } from '../store/tags.data';
import { makeMapFromListOfObjectsAndKey } from '../helpers/helpers';
import { BrandingData, getBrandingData } from '../store/publish.data';
import { JoiAccount } from '../store/accounts.data';
import { Attendee } from '../store/attendees.data';
import { Dictionary } from '../store/app.data';

const populateTimelineData = (programNodeDateMap: ProgramNodeMap, multiTimeline: DateSchedule, performers: { [pId: string]: Performer }, eventProgramOrder: string[]) => {


  programNodeDateMap.forEach(programNode => {
    if (programNode.subschedules) { // program schedule with activity subschedules
      const schedule = { ...programNode.schedule, sessions: [...programNode.schedule.sessions.filter(sess => sess.type !== 'break')] }
      // console.log(programNode.day, { programNode, schedule })
      if (programNode.schedule.type === 'program') {
        programNode.subschedules.forEach(subSchedule => schedule.sessions = schedule.sessions.concat(subSchedule.sessions))
      } else {
        programNode.subschedules.forEach(subSchedule => subSchedule.sessions.forEach(activity => activity.scheduleId = schedule.id )) // <== don't want to add a standalone's activities into the main schedule's sessions. HOWEVER, do want those activities to know about the main "schedule" (the standalone schedule)
      }
      schedule.sessions.sort(sortOnsiteTimelineBrick)
      schedule.sessions.forEach(sess => { 
        sess.scheduleId = schedule.scheduleId
        sess.stagingRequests = getValidSortedStagingRequests(sess.stagingRequests || [], performers)
      })
      multiTimeline.programs.push(schedule)
    } else {
      multiTimeline.programs.push(programNode.schedule)
    }
  })

  multiTimeline.programs.sort((a, b) => {
    const aIndex = (eventProgramOrder || []).indexOf(a.scheduleId)
    const bIndex = (eventProgramOrder || []).indexOf(b.scheduleId)
  
    const aInList = aIndex !== -1
    const bInList = bIndex !== -1
  
    if (aInList && bInList) {
      return aIndex - bIndex
    }
  
    if (aInList) return -1
    if (bInList) return 1
  
    return a.order - b.order
  })

}

export const initializeFullMultiTimeline = (programNodesByDate: ProgramNodeDateMap, performers: { [pId: string]: Performer }, eventProgramOrder: string[]): DateSchedule[] => {

	if (!programNodesByDate) {
		return []
  }
  const dates = Object.keys(programNodesByDate)
  // console.log({ dates: jsonCopyObject(dates), programNodesByDate })
  // dates.forEach(date => console.log(date, programNodesByDate[date]))

  const multiTimelines: DateSchedule[] = dates.map(dateId => ({id: dateId, date: str2Date(dateId), programs: []}))
  // console.log({ dates, multiTimelines, multiTimelinesCopied: jsonCopyObject(multiTimelines) })
  dates.forEach((dateId, index) => {
    // console.log(dateId)
    populateTimelineData(programNodesByDate[dateId], multiTimelines[index], performers, eventProgramOrder)
  })
	return multiTimelines
}

export const initializeMultiTimeline = (programNodesByDate: ProgramNodeDateMap, dateId: string, performers: { [pId: string]: Performer }, eventProgramOrder: string[]): DateSchedule => {

	if (!dateId || !programNodesByDate || !programNodesByDate[dateId]) {
		return {id: dateId, date: new Date(), programs: []}
  }
  const multiTimeline = {id: dateId, date: str2Date(dateId), programs: []}
  populateTimelineData(programNodesByDate[dateId], multiTimeline, performers, eventProgramOrder)
  // console.log(dateId, { multiTimeline: jsonCopyObject(multiTimeline )})
	return multiTimeline
}


export const convertMultiTimelineToSingleTimeline = (multiTimeline: DateSchedule, assets: Dictionary<Asset>, fullScheduleOrder: string[]) => {
	let bricks: OnsiteTimelineBrick[] = [];
	multiTimeline.programs.forEach(p => {
		bricks = bricks.concat(p.sessions.map(sess => {
      const brick: OnsiteTimelineBrick = {
				...sess,
				program_title: p.title,
        program_location: p.location,
        venueId: p.venueId,
				program_scheduleId: p.scheduleId,
				program_timeZoneId: p.timeZoneId || getDefaultTimeZoneDetails().timeZoneId,
        program_type: p.type,
        isStandalone: p.order > 99,
        presentationAssets: sess.type === 'session' ? (sess.presentationAssetIds || []).map(id => assets[id]).filter(a => a) : [],
        otherAssets: (sess.assetIds || []).map(id => assets[id]).filter(a => a),
      }
      return brick
		}))
		bricks.sort((a,b) => sortOnsiteTimelineBrick(a, b, fullScheduleOrder))
	})
	const singleTimeline = { date: multiTimeline.date, bricks: bricks, dateId: multiTimeline.id }

	return singleTimeline
}

export const convertFullMultiTimelineToSingleTimeline = (multiTimelines: DateSchedule[], assets: Dictionary<Asset>, fullScheduleOrder: string[]) =>
  multiTimelines.map(t => convertMultiTimelineToSingleTimeline(t, assets, fullScheduleOrder))

export interface BriefStatusBySessAndPerfId { [sId: string]: { [pId: string]: string }}

export const getSessionList = (programs: Dictionary<Program>) => flatten(typedValues(programs).map(p => typedValues(p.sessions_obj)))
export const getSessionMapFromPrograms = (programs: Dictionary<Program>) => makeMapFromListOfObjectsAndKey(getSessionList(programs), 'id')

export const getBriefStatusBySessionAndPerformerId = (performers: Dictionary<Performer>, programs: Dictionary<Program>) => {
  const briefStatusBySessionAndPerformerId:BriefStatusBySessAndPerfId = {}
  Object.values(programs).forEach(program => 
    Object.values(program.sessions_obj).filter(({ performerBriefStatus }) => performerBriefStatus).forEach(({ id, performerBriefStatus }) => {
      briefStatusBySessionAndPerformerId[id] = {}
      Object.entries(performerBriefStatus).filter(([pId, _]) => !!performers[pId]).forEach(([ performerId, status ]) => {
        briefStatusBySessionAndPerformerId[id][performerId] = status
      })
    }        
  ))
  return briefStatusBySessionAndPerformerId
}

export interface BriefTimelineInputs {
  activities: OnsiteActivity[]
  schedules: OnsiteSchedule[]
  programs: Program[]
  assets: Dictionary<Asset>
  performerId?: string
  performers: { [pId: string]: Performer }
  exhibitorId?: string
  exhibitors?: { [eId:string]: Exhibitor }
  resourceId?: string
  resources?: { [rId:string]: JoiResource}
  stands: { [sId:string]: ExhibitorStand }
  attendeeId?: string
  eventProgramOrder: string[]
}

export const sortProgramNodesList = (programNodesList: ProgramNode[], programOrder: string[]): ProgramNode[] => {
	programNodesList.sort((n1, n2) => {
    const p1Index = programOrder.findIndex(id => id === n1.schedule.scheduleId)
    const p2Index = programOrder.findIndex(id => id === n2.schedule.scheduleId)
    // console.log({ p1Index, p2Index, n1: n1.schedule.title, n2: n2.schedule.title })
    if (p1Index > -1 && p2Index > -1) return p1Index - p2Index
    else if (p1Index > -1) return -1
    else if (p2Index > -1) return 1
    else return n1.schedule.order - n2.schedule.order // standalones go by original "order"
  })
  return programNodesList
}




export const getFullBriefTimeline = (inputs: BriefTimelineInputs): OnsiteSingleTimeline[] => {
  const { activities = [], schedules, programs, performerId = null, exhibitorId = null , exhibitors, performers, resources, stands, assets, resourceId, eventProgramOrder, attendeeId } = inputs
  
  const programMap = makeMapFromListOfObjectsAndKey(programs, 'id')
  // console.log('getFullBriefTimeline', { programs, programMap })
  
  if (!schedules || !programs || (!performerId && !exhibitorId && !resourceId && !attendeeId) || !assets) {
    console.log("SOMETHING MISSING!", { activities, schedules, programs, performerId, exhibitorId, resourceId, attendeeId, exhibitors, performers, stands, assets })
    return null
  }

  const { programNodesByDate } = getOnsiteSchedules(activities, schedules, programs, true)
  const allProgramNodes = flatten(Object.values(programNodesByDate).map(programNodesMap => Array.from(programNodesMap.values())))
  sortProgramNodesList(allProgramNodes, eventProgramOrder)
  const fullScheduleOrder = allProgramNodes.map(p => p.schedule.scheduleId)

  const multiTimelines = initializeFullMultiTimeline(programNodesByDate, performers, eventProgramOrder)
  // console.log({ programNodesByDate, performers, multiTimelines: jsonCopyObject(multiTimelines) })

  const filters: FilterGroup = { count: 1 }
  if (performerId) filters.performers = [performerId]
  if (exhibitorId) filters.exhibitors = [exhibitorId]
  if (resourceId) filters.resources = [resourceId]
  if (attendeeId) filters.attendees = [attendeeId]
  // console.log({ filters })

  const filteredMultiTimelines = filterTimeline(multiTimelines, filters, false, exhibitors, performerId ? 'performerBrief' : exhibitorId ? 'exhibitorBrief' : attendeeId ? 'attendeeBrief' : 'resourceBrief', stands, programMap)
  // console.log({ filteredMultiTimelines: jsonCopyObject(filteredMultiTimelines) })


  const timeline = convertFullMultiTimelineToSingleTimeline(filteredMultiTimelines, assets, fullScheduleOrder)
  // console.log({ timeline: jsonCopyObject(timeline)})

  return timeline
}

export interface FullSharedFilteredOSSTimelineAndShareableAndPerformersData {
  timeline: OnsiteSingleTimeline[]
  performers: { [pId: string]: Performer }
  sponsors: { [pId: string]: Sponsor }
  sponsorTypes: { [tId:string]: Ranking }
  venues: { [vId: string]: Venue }
  info: ShareOSSInfo
  showActivityStatus: boolean
  hideJoi?: boolean
  labelMaps?: LabelMaps
  tagMap: { [vId:string]: TagPanelData }
  brandingData: BrandingData
  performerRoles: { [pId: string]: Ranking }
}

export const getFullSharedFilteredOSSTimelineAndShareableAndPerformers = (
  activities: OnsiteActivity[] = [], 
  schedules: OnsiteSchedule[],
  allPrograms: Program[], 
  info: ShareOSSInfo, 
  event: JoiEvent,
  performers: { [pId: string]: Performer } = {}, 
  labelMaps: LabelMaps, 
  workforceGroupsById: { [id:string]: WorkforceGroup }, 
  workersByEmail: { [key:string]: WFWorker },
  sponsors: { [pId: string]: Sponsor } = {},
  sponsorTypes: { [tId:string]: Ranking },
  exhibitors: { [eId:string]: Exhibitor } = {},
  stands: { [sId:string]: ExhibitorStand },
  assets: Dictionary<Asset>,
  resources: { [rId: string]: JoiResource },
  account: Partial<JoiAccount>,
  eventProgramOrder: string[],
  attendees: Dictionary<Attendee>,
): FullSharedFilteredOSSTimelineAndShareableAndPerformersData => {
  if (!schedules || !allPrograms || !info || !event) {
    console.log("SOMETHING MISSING!")
    return null
  }
  info.eventTitle = event? event.title : ''
  info.eventId = event ? event.eventId : ''

  const { excludedFieldIds } = info
  const programs = jsonCopyObject(allPrograms)
  programs.filter(p => !p.hasOwnProperty('setups')).forEach(p => p.setups = {}) 
  if (excludedFieldIds) {
    if (excludedFieldIds.includes('setups')) programs.forEach(p => p.setups = {})
    if (excludedFieldIds.includes('access')) programs.forEach(p => Object.values(p.setups).forEach(s => {
      s.access = null 
      s.accessDay = null
    }))
    if (excludedFieldIds.includes('clear')) programs.forEach(p => Object.values(p.setups).forEach(s => {
      s.clear = null 
      s.clearDay = null
    }))
  }

  const { programNodesByDate } = getOnsiteSchedules(activities, schedules, programs, true)
  const multiTimelines = initializeFullMultiTimeline(programNodesByDate, performers, event.programOrder)
  
  const filters = info.filters || { count: 0 }
  const showActivityStatus = !excludedFieldIds || !excludedFieldIds.includes('activityStatus') // NOTE:  showActivityStatus will only filter through here in the app

  filterOnsiteTimelineFilters(filters,  multiTimelines, performers, labelMaps, workforceGroupsById, workersByEmail, sponsors, exhibitors, event?.exhibitorStands || {}, resources, attendees)
  // console.log({ filters })

  // console.log("getFullSharedFilteredOSSTimelineAndShareableAndPerformers")
  const filteredMultiTimelines = filters && !filters.count ? multiTimelines : filterTimeline(multiTimelines, filters, true, exhibitors, 'oss', stands, makeMapFromListOfObjectsAndKey(programs, 'id'))
  // console.log({ filteredMultiTimelines })

  const allProgramNodes = flatten(Object.values(programNodesByDate).map(programNodesMap => Array.from(programNodesMap.values())))
  sortProgramNodesList(allProgramNodes, eventProgramOrder)
  const fullScheduleOrder = allProgramNodes.map(p => p.schedule.scheduleId)
  const timeline = convertFullMultiTimelineToSingleTimeline(filteredMultiTimelines, assets, fullScheduleOrder)
  // console.log({ timeline })
  
  const filteredTimelineByFields = excludedFieldIds && excludedFieldIds.length ? filterTimelineByFields(excludedFieldIds, timeline) : timeline
  // console.log({ filteredTimelineByFields })

  const filteredPerformers = filterPerformersByExcludedOSSFieldsAndFilters(info, performers, filters)
  // console.log({ filteredPerformers })
  const filteredSponsors = filterSponsorsByExcludedOSSFieldsAndFilters(info, sponsors, filters)

  const filteredInfo = filterShareableByExcludedOSSFields(info)
  // console.log({ filteredInfo })

  const tagMap = getAllTagPanelData(programs, activities, workersByEmail, performers, sponsors, exhibitors, resources, attendees, false)
  const allSessionAndActivityIds = getSet(flatten(filteredTimelineByFields.map(t => t.bricks)), 'id')
  Object.keys(tagMap).filter(id => !allSessionAndActivityIds.has(id)).forEach(id => delete tagMap[id])

  return { 
    timeline: filteredTimelineByFields, 
    performers: filteredPerformers,
    info: filteredInfo, 
    showActivityStatus,
    sponsors: filteredSponsors,
    sponsorTypes,
    venues: event ? event.venues : {},
    tagMap,
    brandingData: getBrandingData({ acc: account, event, brandingSelectData: { type: 'forms', brandFormType: 'schedules' } }),
    performerRoles: excludedFieldIds.includes("performerRoles") ? null : makeMapFromListOfObjectsAndKey(event?.performerRoles || [], 'id')
  }
}
